import React, { useState, useEffect } from 'react';

const FileList = ({ apiBaseUrl = 'http://localhost:5000/api' }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deletingFile, setDeletingFile] = useState(null);

    // Get Cognito JWT token from storage
    const getAuthToken = () => {
        return localStorage.getItem('cognitoIdToken');
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Fetch files from API
    const fetchFiles = async () => {
        setLoading(true);
        setError(null);

        try {
            const token = getAuthToken();

            if (!token) {
                throw new Error('Not authenticated. Please log in.');
            }

            const response = await fetch(`${apiBaseUrl}/files`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to fetch files');
            }

            const data = await response.json();
            setFiles(data.files || []);

        } catch (err) {
            console.error('Fetch error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Delete file
    const handleDelete = async (filename) => {
        // Confirm deletion
        if (!window.confirm(`Are you sure you want to delete "${filename}"?`)) {
            return;
        }

        setDeletingFile(filename);

        try {
            const token = getAuthToken();

            if (!token) {
                throw new Error('Not authenticated. Please log in.');
            }

            const response = await fetch(`${apiBaseUrl}/files/${encodeURIComponent(filename)}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to delete file');
            }

            // Remove file from list
            setFiles(files.filter(f => f.filename !== filename));

        } catch (err) {
            console.error('Delete error:', err);
            alert(`Failed to delete file: ${err.message}`);
        } finally {
            setDeletingFile(null);
        }
    };

    // Load files on component mount
    useEffect(() => {
        fetchFiles();
    }, []);

    return (
        <div style={{ padding: '2rem', background: '#f8f9fa', minHeight: '100vh' }}>
            <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
                <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '2rem'
                }}>
                    <h1 style={{ color: '#000', margin: 0 }}>My Files</h1>
                    <button
                        onClick={fetchFiles}
                        disabled={loading}
                        style={{
                            background: '#3498db',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            padding: '0.75rem 1.5rem',
                            fontSize: '0.95rem',
                            fontWeight: '500',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            opacity: loading ? 0.6 : 1
                        }}
                    >
                        {loading ? 'Refreshing...' : '🔄 Refresh'}
                    </button>
                </div>

                {/* Error Message */}
                {error && (
                    <div style={{
                        background: '#fee',
                        border: '1px solid #fcc',
                        color: '#c33',
                        padding: '1rem',
                        borderRadius: '5px',
                        marginBottom: '1.5rem'
                    }}>
                        <strong>Error:</strong> {error}
                    </div>
                )}

                {/* Loading State */}
                {loading && !error && (
                    <div style={{
                        textAlign: 'center',
                        padding: '3rem',
                        color: '#666'
                    }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            border: '4px solid #f3f3f3',
                            borderTop: '4px solid #3498db',
                            borderRadius: '50%',
                            margin: '0 auto 1rem',
                            animation: 'spin 1s linear infinite'
                        }}></div>
                        Loading files...
                    </div>
                )}

                {/* Empty State */}
                {!loading && !error && files.length === 0 && (
                    <div style={{
                        background: 'white',
                        borderRadius: '10px',
                        padding: '3rem',
                        textAlign: 'center'
                    }}>
                        <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>📁</div>
                        <h2 style={{ color: '#000', marginBottom: '0.5rem' }}>No files yet</h2>
                        <p style={{ color: '#666', marginBottom: '1.5rem' }}>
                            Upload your first file to get started
                        </p>
                        <a
                            href="/upload"
                            style={{
                                display: 'inline-block',
                                background: '#27ae60',
                                color: 'white',
                                textDecoration: 'none',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '5px',
                                fontWeight: '500'
                            }}
                        >
                            Upload Files
                        </a>
                    </div>
                )}

                {/* Files List */}
                {!loading && !error && files.length > 0 && (
                    <div style={{ background: 'white', borderRadius: '10px', overflow: 'hidden' }}>
                        <div style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid #e0e0e0',
                            background: '#f8f9fa'
                        }}>
                            <p style={{ margin: 0, color: '#000', fontWeight: '500' }}>
                                {files.length} file{files.length !== 1 ? 's' : ''} uploaded
                            </p>
                        </div>

                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ 
                                width: '100%', 
                                borderCollapse: 'collapse'
                            }}>
                                <thead>
                                    <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                                        <th style={{ 
                                            padding: '1rem', 
                                            textAlign: 'left',
                                            color: '#000',
                                            fontWeight: '600'
                                        }}>
                                            File Name
                                        </th>
                                        <th style={{ 
                                            padding: '1rem', 
                                            textAlign: 'left',
                                            color: '#000',
                                            fontWeight: '600'
                                        }}>
                                            Size
                                        </th>
                                        <th style={{ 
                                            padding: '1rem', 
                                            textAlign: 'left',
                                            color: '#000',
                                            fontWeight: '600'
                                        }}>
                                            Uploaded
                                        </th>
                                        <th style={{ 
                                            padding: '1rem', 
                                            textAlign: 'center',
                                            color: '#000',
                                            fontWeight: '600'
                                        }}>
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {files.map((file, index) => (
                                        <tr 
                                            key={index}
                                            style={{ 
                                                borderBottom: '1px solid #e0e0e0',
                                                background: index % 2 === 0 ? 'white' : '#fafafa'
                                            }}
                                        >
                                            <td style={{ 
                                                padding: '1rem',
                                                color: '#000'
                                            }}>
                                                <div style={{ 
                                                    display: 'flex', 
                                                    alignItems: 'center',
                                                    gap: '0.5rem'
                                                }}>
                                                    <span>📄</span>
                                                    <span style={{ fontWeight: '500' }}>
                                                        {file.filename}
                                                    </span>
                                                </div>
                                            </td>
                                            <td style={{ 
                                                padding: '1rem',
                                                color: '#666'
                                            }}>
                                                {formatFileSize(file.size)}
                                            </td>
                                            <td style={{ 
                                                padding: '1rem',
                                                color: '#666'
                                            }}>
                                                {formatDate(file.last_modified)}
                                            </td>
                                            <td style={{ 
                                                padding: '1rem',
                                                textAlign: 'center'
                                            }}>
                                                <button
                                                    onClick={() => handleDelete(file.filename)}
                                                    disabled={deletingFile === file.filename}
                                                    style={{
                                                        background: deletingFile === file.filename ? '#ccc' : '#e74c3c',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '5px',
                                                        padding: '0.5rem 1rem',
                                                        fontSize: '0.85rem',
                                                        fontWeight: '500',
                                                        cursor: deletingFile === file.filename ? 'not-allowed' : 'pointer'
                                                    }}
                                                >
                                                    {deletingFile === file.filename ? 'Deleting...' : '🗑️ Delete'}
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            <style>
                {`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}
            </style>
        </div>
    );
};

export default FileList;

